import { Card, Col, Row, Empty, Typography, Select, Space, Tag } from "antd";
import { useMemo, useState } from "react";
import { format } from "date-fns";
import { TrophyOutlined, CalendarOutlined } from "@ant-design/icons";

const { Text } = Typography;
const { Option } = Select;

const AwardsTab = ({ institution }) => {
  const { awards = [] } = institution;

  const [selectedYear, setSelectedYear] = useState("All");
  const [selectedAccolade, setSelectedAccolade] = useState("All");
  const [selectedRanking, setSelectedRanking] = useState("All");

  // Extract filter options
  const years = useMemo(() => {
    const all = awards
      .map((award) => {
        const year = new Date(award.date).getFullYear();
        return isNaN(year) ? null : year.toString();
      })
      .filter((year) => year !== null);
    return ["All", ...Array.from(new Set(all)).sort((a, b) => b - a)];
  }, [awards]);

  const accoladeTypes = useMemo(() => {
    const types = awards.map((a) => a.accolade?.trim()).filter(Boolean);
    return ["All", ...Array.from(new Set(types))];
  }, [awards]);

  const rankings = useMemo(() => {
    const list = awards.map((a) => a.ranking?.trim()).filter(Boolean);
    return ["All", ...Array.from(new Set(list))];
  }, [awards]);

  // Filtered awards
  const filtered = useMemo(() => {
    return awards.filter((award) => {
      const matchYear =
        selectedYear === "All" ||
        new Date(award.date).getFullYear().toString() === selectedYear;

      const matchAccolade =
        selectedAccolade === "All" ||
        (award.accolade &&
          award.accolade.toLowerCase() === selectedAccolade.toLowerCase());

      const matchRanking =
        selectedRanking === "All" ||
        (award.ranking &&
          award.ranking.toLowerCase() === selectedRanking.toLowerCase());

      return matchYear && matchAccolade && matchRanking;
    });
  }, [awards, selectedYear, selectedAccolade, selectedRanking]);

  return (
    <div className="pt-5 pb-6">
      <Space className="mb-6 flex-wrap" size="large">
        <Select
          value={selectedYear}
          onChange={setSelectedYear}
          style={{ minWidth: 150 }}
          placeholder="Filter by Year"
        >
          {years.map((year) => (
            <Option key={year} value={year}>
              {year}
            </Option>
          ))}
        </Select>

        <Select
          value={selectedAccolade}
          onChange={setSelectedAccolade}
          style={{ minWidth: 180 }}
          placeholder="Filter by Accolade"
        >
          {accoladeTypes.map((type) => (
            <Option key={type} value={type}>
              {type}
            </Option>
          ))}
        </Select>

        <Select
          value={selectedRanking}
          onChange={setSelectedRanking}
          style={{ minWidth: 160 }}
          placeholder="Filter by Ranking"
        >
          {rankings.map((rank) => (
            <Option key={rank} value={rank}>
              {rank}
            </Option>
          ))}
        </Select>
      </Space>

      {filtered.length === 0 ? (
        <Empty
          description="No awards found for selected filters."
          className="my-10"
        />
      ) : (
        <Row gutter={[24, 24]}>
          {filtered.map((award) => (
            <Col key={award.id} xs={24} sm={12} md={6}>
              <Card
                title={
                  <div className="text-base font-semibold text-gray-800 flex items-center gap-2">
                    <TrophyOutlined className="text-yellow-600" />
                    {award.name}
                  </div>
                }
                className="shadow-sm hover:shadow transition-all border border-gray-100"
              >
                <div className="text-sm text-gray-700 space-y-2">
                  <p className="text-wrap">
                    <strong>Accolade:</strong>{" "}
                    <Tag className="!text-wrap !mt-1" color="blue">
                      {award.accolade || "—"}
                    </Tag>
                  </p>

                  <p>
                    <strong>Ranking:</strong>{" "}
                    <Tag color="green">{award.ranking || "—"}</Tag>
                  </p>

                  {award.date && (
                    <p>
                      <CalendarOutlined className="mr-1 text-gray-500" />
                      <strong>Date:</strong>{" "}
                      <Tag>{format(new Date(award.date), "do MMM yyyy")}</Tag>
                    </p>
                  )}
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default AwardsTab;
